import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink, Lock, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useSettings } from "@/context/SettingsContext";

const serviceLinks = [
  { label: "All Services", href: "/services" },
  { label: "Tax Preparation", href: "/tax-prep" },
  { label: "Bookkeeping", href: "/bookkeeping" },
  { label: "Business Tax", href: "/business" },
  { label: "Entity Formation", href: "/entity-formation" },
  { label: "Non-Profits", href: "/nonprofit" },
  { label: "Notary", href: "/notary" },
];

const mainLinks = [
  { label: "Pricing", href: "/#pricing" },
  { label: "Make a Payment", href: "/payments" },
  { label: "AI Automation", href: "/ai-automation" },
  { label: "Resources", href: "/resources" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

// Flat list for the mobile menu
const navItems = [
  { label: "Services", href: "/services" },
  { label: "Tax Prep", href: "/tax-prep" },
  { label: "Bookkeeping", href: "/bookkeeping" },
  { label: "Business", href: "/business" },
  { label: "Entity Formation", href: "/entity-formation" },
  { label: "Non-Profits", href: "/nonprofit" },
  { label: "Notary", href: "/notary" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Make a Payment", href: "/payments" },
  { label: "AI Automation", href: "/ai-automation" },
  { label: "Resources", href: "/resources" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const openBooking = () => window.dispatchEvent(new CustomEvent("open-booking"));

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const settings = useSettings();
  const PORTAL_URL = settings.portal_url;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      {/* Top utility bar */}
      <div className="bg-navy text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm">
          <a 
            href="tel:+12012537674" 
            className="font-mono text-gray-300 hover:text-bronze transition-colors"
            data-testid="header-phone"
          >
            201-253-7674
          </a>
          <div className="flex items-center gap-2 sm:gap-4">
            <a
              href={`mailto:${settings.business_email}`}
              className="hidden sm:inline text-gray-300 hover:text-bronze transition-colors text-xs sm:text-sm"
              data-testid="header-email"
            >
              {settings.business_email}
            </a>
            <span className="hidden sm:inline text-gray-600">|</span>
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-bronze hover:text-bronze/80 transition-colors text-xs sm:text-sm font-medium"
              data-testid="header-portal-link"
            >
              <Lock className="w-3.5 h-3.5" />
              Client Portal
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center shrink-0 mr-4"
            data-testid="header-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_dataxguy-web/artifacts/2eefwwd8_dataxguy-Dataxguy%20logo.jpg" 
              alt="DA TAXGUY & ASSOCIATES"
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-7">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1 font-body text-sm font-medium text-gray-700 hover:text-bronze transition-colors outline-none data-[state=open]:text-bronze"
                data-testid="nav-services-trigger"
              >
                Services
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {serviceLinks.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link
                      to={item.href}
                      className="cursor-pointer font-body"
                      data-testid={`nav-dd-${item.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    >
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {mainLinks.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`font-body text-sm font-medium transition-colors hover:text-bronze whitespace-nowrap ${
                  location.pathname === item.href ? "text-bronze" : "text-gray-700"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Button
              asChild
              className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-5 font-body font-semibold"
              data-testid="header-start-btn"
            >
              <Link to="/start">Start Your Return</Link>
            </Button>
            <Button
              variant="outline"
              onClick={openBooking}
              className="border-navy text-navy hover:bg-navy hover:text-white rounded-full px-5 font-body font-medium"
              data-testid="header-book-btn"
            >
              Book a Consultation
            </Button>
          </div>

          {/* Mobile: persistent CTA + menu */}
          <div className="lg:hidden flex items-center gap-2">
            <Button
              asChild
              size="sm"
              className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-4 font-body font-semibold"
              data-testid="mobile-start-btn"
            >
              <Link to="/start">Start Return</Link>
            </Button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-80 overflow-y-auto">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Site navigation and quick actions</SheetDescription>
                <div className="flex flex-col h-full py-6">
                  <div className="flex items-center justify-between mb-8">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_dataxguy-web/artifacts/2eefwwd8_dataxguy-Dataxguy%20logo.jpg" 
                      alt="DA TAXGUY"
                      className="h-10 w-auto"
                    />
                    <SheetClose asChild>
                      <Button variant="ghost" size="icon">
                        <X className="h-5 w-5" />
                      </Button>
                    </SheetClose>
                  </div>
                  
                  <nav className="flex flex-col gap-4">
                    {navItems.map((item) => (
                      <SheetClose asChild key={item.href}>
                        <Link
                          to={item.href}
                          className={`font-body text-lg py-2 transition-colors hover:text-bronze ${
                            location.pathname === item.href ? "text-bronze" : "text-gray-700"
                          }`}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    ))}
                  </nav>

                  <div className="mt-auto pt-8 flex flex-col gap-3">
                    <SheetClose asChild>
                      <Button
                        asChild
                        className="w-full bg-bronze hover:bg-bronze/90 text-navy rounded-full font-body font-semibold"
                        data-testid="mobile-start-cta"
                      >
                        <Link to="/start">Start Your Return</Link>
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button
                        variant="outline"
                        onClick={openBooking}
                        className="w-full border-navy text-navy hover:bg-navy hover:text-white rounded-full font-body"
                        data-testid="mobile-book-btn"
                      >
                        Book a Consultation
                      </Button>
                    </SheetClose>
                    <Button
                      asChild
                      variant="ghost"
                      className="w-full text-bronze rounded-full font-body"
                      data-testid="mobile-portal-btn"
                    >
                      <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                        <Lock className="w-4 h-4 mr-2" /> Client Portal Login
                      </a>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}
